import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, Package, Barcode, AlertTriangle } from 'lucide-react';

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export default function ProductSearch({ products = [], onAddToCart, onBarcodeSearch, inputRef, autoRefocus = true, emptyResultsLabel = 'No products found' }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const innerRef = useRef(null);
  const searchRef = inputRef || innerRef;
  const containerRef = useRef(null);

  const nameOf = (p) => p.productName || p.name || '';
  const codeOf = (p) => p.barcode || p.serialNumber || p.sku || '';
  const stockOf = (p) => p.currentStock ?? p.stock ?? p.quantity ?? 0;
  const priceOf = (p) => p.salePrice ?? p.price ?? 0;

  const filtered = query.trim()
    ? products.filter((p) => {
        const q = query.toLowerCase();
        return (
          nameOf(p).toLowerCase().includes(q) ||
          codeOf(p).toLowerCase().includes(q)
        );
      })
    : [];

  const debouncedSearch = useCallback(
    debounce((val) => {
      if (val.trim()) {
        setIsOpen(true);
        setActiveIndex(0);
      } else {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }, 200),
    []
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  useEffect(() => {
    if (isOpen && Array.isArray(filtered) && filtered.length > 0 && activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [isOpen, filtered.length, activeIndex]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollActiveIntoView = useCallback((el) => {
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, []);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' && query.trim()) {
        onBarcodeSearch?.(query.trim());
        setQuery('');
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && filtered[activeIndex]) {
          handleSelect(filtered[activeIndex]);
        } else if (query.trim()) {
          onBarcodeSearch?.(query.trim());
          setQuery('');
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelect = (product) => {
    onAddToCart?.(product);
    setQuery('');
    setIsOpen(false);
    setActiveIndex(-1);
    if (autoRefocus) searchRef.current?.focus();
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
          <Search size={20} />
        </div>
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim() && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search products by name, barcode, or SKU... (F1)"
          autoComplete="off"
          className="w-full pl-10 pr-12 py-3.5 text-base bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <button
            type="button"
            onClick={() => searchRef.current?.focus()}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Scan barcode"
          >
            <Barcode size={18} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50 max-h-80 overflow-y-auto scrollbar-thin"
          >
            {filtered.map((product, idx) => {
              const outOfStock = stockOf(product) <= 0;
              return (
                <motion.button
                  ref={idx === activeIndex ? scrollActiveIntoView : undefined}
                  key={product.productId || product._id || product.id || idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.03 }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => !outOfStock && handleSelect(product)}
                  disabled={outOfStock}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    outOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    activeIndex === idx
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {product.image ? (
                      <img src={product.image} alt={nameOf(product)} className="w-full h-full object-cover" />
                    ) : (
                      <Package size={18} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      outOfStock ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'
                    }`}>
                      {nameOf(product)}
                    </p>
                    {codeOf(product) && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {codeOf(product)}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-semibold ${
                      outOfStock ? 'text-gray-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      Rs. {priceOf(product)}
                    </p>
                    <p className={`text-xs ${
                      outOfStock
                        ? 'text-red-400 dark:text-red-400'
                        : stockOf(product) <= 5
                        ? 'text-amber-500'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {outOfStock ? (
                        <span className="flex items-center gap-1">
                          <AlertTriangle size={10} />
                          Out of stock
                        </span>
                      ) : (
                        <>Stock: {stockOf(product)} pcs</>
                      )}
                    </p>
                  </div>
                  {!outOfStock && (
                    <div className="flex-shrink-0 ml-1">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
                        <Plus size={16} />
                      </div>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {isOpen && query.trim() && filtered.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 text-center z-50"
        >
          <Search size={32} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{emptyResultsLabel}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Press Enter to search by barcode
          </p>
        </motion.div>
      )}
    </div>
  );
}
